import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Droplets, Wallet, TrendingUp, Clock, Car, ListOrdered } from 'lucide-react';
import { Link } from 'wouter';
import { api } from '@/lib/api';
import { formatPriceWithCurrency, formatRelativeTime, VEHICLE_LABELS } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSocket, useSocketEvent } from '@/hooks/useSocket';
import StatsCard from '@/components/StatsCard';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

interface DashboardData {
  today: {
    totalWashes: number;
    sedanWashes: number;
    suvWashes: number;
    largeSuvWashes: number;
    revenue: number;
  };
  week: {
    totalWashes: number;
    revenue: number;
  };
  month: {
    totalWashes: number;
    revenue: number;
  };
  queue: {
    waiting: number;
    washing: number;
    ready: number;
  };
  recentWashes: Array<{
    id: string;
    plateNumber: string;
    vehicleType: string;
    customerName: string;
    washedAt: string;
    washNumber: number;
  }>;
}

export default function HomePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { socket } = useSocket(user?.shopId);

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['owner', 'dashboard'],
    queryFn: () => api.get('/owner/dashboard'),
    refetchInterval: 30000,
  });

  // Real-time: refresh dashboard when queue changes
  useSocketEvent(socket, 'shop:queue_changed', () => {
    queryClient.refetchQueries({ queryKey: ['owner', 'dashboard'] });
  });
  useSocketEvent(socket, 'shop:stats_updated', () => {
    queryClient.refetchQueries({ queryKey: ['owner', 'dashboard'] });
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const stats = data || {
    today: { totalWashes: 0, sedanWashes: 0, suvWashes: 0, largeSuvWashes: 0, revenue: 0 },
    week: { totalWashes: 0, revenue: 0 },
    month: { totalWashes: 0, revenue: 0 },
    queue: { waiting: 0, washing: 0, ready: 0 },
    recentWashes: [],
  };

  const queueTotal = stats.queue.waiting + stats.queue.washing + stats.queue.ready;

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold">لوحة التحكم</h1>
            <p className="text-white/80 text-sm">ملخص يومك</p>
          </div>
        </div>
      </div>

      {/* Today Stats */}
      <div>
        <h2 className="text-sm font-bold text-gray-500 mb-3">اليوم</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatsCard
            title="الغسلات"
            value={stats.today.totalWashes}
            subtitle="اليوم"
            icon={Droplets}
            bgColor="bg-blue-100"
            iconColor="text-blue-600"
          />
          <StatsCard
            title="الإيرادات"
            value={formatPriceWithCurrency(stats.today.revenue)}
            subtitle="اليوم"
            icon={Wallet}
            bgColor="bg-green-100"
            iconColor="text-green-600"
          />
          <StatsCard
            title="الطابور"
            value={queueTotal}
            subtitle={`${stats.queue.washing} يُغسل | ${stats.queue.ready} جاهز`}
            icon={ListOrdered}
            bgColor="bg-amber-100"
            iconColor="text-amber-600"
          />
          <StatsCard
            title="الأسبوع"
            value={stats.week.totalWashes}
            subtitle={formatPriceWithCurrency(stats.week.revenue)}
            icon={TrendingUp}
            bgColor="bg-purple-100"
            iconColor="text-purple-600"
          />
        </div>
      </div>

      {/* Month Summary */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">ملخص الشهر</h2>
          <Link href="/analytics" className="text-sm text-primary-600 hover:text-primary-700">
            عرض التفاصيل
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.month.totalWashes}</p>
            <p className="text-xs text-gray-500">غسلة</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{formatPriceWithCurrency(stats.month.revenue)}</p>
            <p className="text-xs text-gray-500">إيرادات</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{queueTotal}</p>
            <p className="text-xs text-gray-500">في الطابور الآن</p>
          </div>
        </div>
      </div>

      {/* Today's Vehicle Breakdown */}
      {stats.today.totalWashes > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-card">
          <h2 className="text-base font-bold text-gray-900 mb-4">أنواع السيارات اليوم</h2>
          <div className="space-y-3">
            {[
              { type: 'sedan', count: stats.today.sedanWashes, color: 'bg-blue-500' },
              { type: 'suv', count: stats.today.suvWashes, color: 'bg-amber-500' },
              { type: 'large_suv', count: stats.today.largeSuvWashes, color: 'bg-purple-500' },
            ].map((item) => (
              <div key={item.type}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{VEHICLE_LABELS[item.type]?.ar}</span>
                  <span className="font-bold text-gray-900">{item.count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.color} rounded-full transition-all`}
                    style={{
                      width: `${stats.today.totalWashes > 0 ? (item.count / stats.today.totalWashes) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Washes */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">آخر الغسلات</h2>
          <Link href="/history" className="text-sm text-primary-600 hover:text-primary-700">
            عرض الكل
          </Link>
        </div>

        {stats.recentWashes.length === 0 ? (
          <EmptyState icon={Droplets} title="لا توجد غسلات اليوم" />
        ) : (
          <div className="space-y-3">
            {stats.recentWashes.slice(0, 5).map((wash) => (
              <div
                key={wash.id}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <Car className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    غسلة #{wash.washNumber}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {wash.plateNumber} - {VEHICLE_LABELS[wash.vehicleType]?.ar}
                    {wash.customerName && ` - ${wash.customerName}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
                  <Clock className="w-3 h-3" />
                  <span>{formatRelativeTime(wash.washedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-3">
        <Link href="/queue" className="bg-white rounded-2xl p-4 shadow-card hover:shadow-card-hover transition-shadow">
          <ListOrdered className="w-6 h-6 text-primary-600 mb-2" />
          <p className="font-medium text-gray-900">الطابور المباشر</p>
          <p className="text-xs text-gray-500">تتبع السيارات</p>
        </Link>
        <Link href="/settlements" className="bg-white rounded-2xl p-4 shadow-card hover:shadow-card-hover transition-shadow">
          <Wallet className="w-6 h-6 text-green-600 mb-2" />
          <p className="font-medium text-gray-900">التسويات</p>
          <p className="text-xs text-gray-500">المدفوعات والأرباح</p>
        </Link>
      </div>
    </div>
  );
}
